import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  isIncomingFormMessage,
  postBookingContext,
} from '../../_utilities/bookingPostMessage'
import type { BookingFormIframeProps } from './types'
import { IframeElement, IframeOverlay, IframeShell } from './styled'

/**
 * Renders the agent's configured custom form (or flow form) as an iframe
 * sourced from `forms.quickcasa.ai`. Handshakes with the embedded form via
 * `postMessage`:
 *
 * - On `qc-form-ready` (sent by the form when it mounts), pushes the booking
 *   context so the form can prefill fields and know it's part of a booking
 *   flow rather than a standalone submission.
 * - On `qc-form-submitted`, surfaces the submission to the parent so the
 *   booking page can call `createbooking` with the extracted guest info +
 *   the form's submission ID and full response payload.
 * - On `qc-form-error`, surfaces the error message for inline display.
 *
 * @param {BookingFormIframeProps} props - Form URL parts, booking context, callbacks.
 * @returns {JSX.Element} The iframe wrapper.
 */
const BookingFormIframe = ({
  formsBaseUrl,
  companyId,
  formType,
  formSlug,
  context,
  onSubmitted,
  onError,
}: BookingFormIframeProps): JSX.Element => {
  const iframeReference = useRef<HTMLIFrameElement | null>(null)
  const [isFormReady, setIsFormReady] = useState<boolean>(false)

  const iframeSource = useMemo(() => {
    const path =
      formType === 'flow'
        ? `/${companyId}/flow/${formSlug}`
        : `/${companyId}/${formSlug}`
    return `${formsBaseUrl}${path}?booking=1`
  }, [formsBaseUrl, companyId, formType, formSlug])

  const handleMessage = useCallback(
    (messageEvent: MessageEvent): void => {
      console.info(
        '[qc-debug] BookingFormIframe handleMessage fired. origin=',
        messageEvent.origin,
        'data=',
        messageEvent.data,
        'passesFilter=',
        isIncomingFormMessage(messageEvent.data),
      )
      if (!isIncomingFormMessage(messageEvent.data)) {
        return
      }
      const incoming = messageEvent.data
      if (incoming.type === 'qc-form-ready') {
        console.info(
          '[qc-debug] qc-form-ready received → flipping isFormReady',
        )
        setIsFormReady(true)
        const target = iframeReference.current?.contentWindow
        if (target) {
          postBookingContext(target, context)
        }
        return
      }
      if (incoming.type === 'qc-form-submitted') {
        onSubmitted(incoming.submission)
        return
      }
      if (incoming.type === 'qc-form-error') {
        onError(incoming.message)
      }
    },
    [context, onSubmitted, onError],
  )

  useEffect(() => {
    const debugAll = (event: MessageEvent): void => {
      console.info(
        '[qc-debug] (capture) window message. origin=',
        event.origin,
        'data=',
        event.data,
      )
    }
    window.addEventListener('message', debugAll, true)
    window.addEventListener('message', handleMessage)
    console.info('[qc-debug] BookingFormIframe listeners attached')
    return () => {
      window.removeEventListener('message', debugAll, true)
      window.removeEventListener('message', handleMessage)
    }
  }, [handleMessage])

  useEffect(() => {
    if (!isFormReady) {
      return
    }
    const target = iframeReference.current?.contentWindow
    if (target) {
      postBookingContext(target, context)
    }
  }, [context, isFormReady])

  return (
    <IframeShell>
      <IframeElement
        ref={iframeReference}
        src={iframeSource}
        title="Booking form"
        allow="clipboard-read; clipboard-write"
      />
      {isFormReady ? null : <IframeOverlay>Loading form…</IframeOverlay>}
    </IframeShell>
  )
}

export default BookingFormIframe
